import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAtom, useSetAtom, useStore } from 'jotai';
import { useTranslation } from 'next-i18next';
import { nanoid } from '@forma/forma-ui-kit';
import { ApiError } from '@/helpers/baseApi';
import { addNotificationAtom } from '@/store/notifications';
import { clearQueryBranch, queryAtomFamily } from '@/store/queryAtomFamily';
import { apiErrorAtom } from '@/store/apiError';

type QueryFn<T> = (signal?: AbortSignal) => Promise<T> | T;
type MutationFn<T> = (...args: any[]) => Promise<T> | T;

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

interface UseQueryOptions<T> {
  skip?: boolean,
  pollingInterval?: number,
  key?: string,
  onSuccess?: (data: T) => void,
}

interface UseMutationOptions {
  resetQueryKeys?: string[],
}

const useQuery = <T>(query: QueryFn<T>, options?: UseQueryOptions<T>) => {
  const { skip = false, pollingInterval, onSuccess } = options || {};
  const key = options?.key ?? query.toString();
  const store = useStore();
  const { t } = useTranslation();
  const queryAtom = queryAtomFamily(key, store);
  const [state, setState] = useAtom(queryAtom);
  const addNotification = useSetAtom(addNotificationAtom);
  const setApiError = useSetAtom(apiErrorAtom);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  // this component did not request itself: another one with the same key was loading
  const waitingRef = useRef(false);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    requestIdRef.current += 1;
    setState((prev) => ({ ...prev, isLoading: false }));
  }, [setState]);

  const fetchData = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++requestIdRef.current;
    waitingRef.current = false;

    setState((prev) => {
      if (prev.isLoading) return prev;
      return { ...prev, isLoading: true, error: undefined };
    });

    try {
      const data = await query(controller.signal);
      if (requestId !== requestIdRef.current) return;
      abortControllerRef.current = null;
      setState({ data, error: undefined, isLoading: false });
      if (onSuccess) onSuccess(data);
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted) return;
      if (requestId !== requestIdRef.current) return;
      abortControllerRef.current = null;

      setState({ data: null, error: error as ApiError, isLoading: false });

      if (error instanceof ApiError) {
        if (error.status === 'FETCH_ERROR') {
          addNotification({ id: 'fetch_error', content: t('errors.fetch_error'), type: 'ERROR' });
          return;
        }
        if (error.status === 500 && error.details) {
          setApiError(error.details);
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const dataString = JSON.stringify(state.data);

  useEffect(() => {
    if (skip) return;
    if (state.data && !pollingInterval) return;
    if (state.isLoading && !pollingInterval) {
      waitingRef.current = true;
      return;
    }

    fetchData();

    if (pollingInterval) {
      timerRef.current = setInterval(fetchData, pollingInterval);
    }

    return () => {
      // the request of this component is cancelled: the shared state must not stay "loading",
      // otherwise the other components with the same key never request the data
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setState((prev) => (prev.isLoading ? { ...prev, isLoading: false } : prev));
      }
      abortControllerRef.current = null;
      requestIdRef.current += 1;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataString, skip, pollingInterval, key]);

  // the component waited for the request of another one, and that request was cancelled: request itself
  useEffect(() => {
    if (skip || pollingInterval || !waitingRef.current) return;
    if (!state.isLoading && !state.data && !state.error) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isLoading]);

  const refetch = useCallback(() => {
    if (!skip) fetchData();
  }, [fetchData, skip]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => state.data, [dataString]) as T;

  return {
    data,
    error: state.error,
    isError: !!state.error,
    isSuccess: !!state.data && !state.error,
    isLoading: state.isLoading,
    refetch,
    abort,
  };
};

const useMutation = <T>(mutation: MutationFn<T>, options?: UseMutationOptions) => {
  const { t } = useTranslation();
  const store = useStore();
  const setApiError = useSetAtom(apiErrorAtom);
  const addNotification = useSetAtom(addNotificationAtom);

  const [data, setData] = useState<T | null>();
  const [error, setError] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    requestIdRef.current += 1;
    setIsLoading(false);
  }, []);

  const fetchData = useCallback(async (...args: any[]) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++requestIdRef.current;

    setIsLoading(true);
    setData(null);

    try {
      const response = await mutation(...args, controller.signal);
      if (requestId !== requestIdRef.current) return;

      setData(response);

      if (options?.resetQueryKeys?.length) {
        options.resetQueryKeys.forEach((key) => {
          clearQueryBranch(key, store);
        });
      }

      return response;
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted) return;
      if (requestId !== requestIdRef.current) return;

      setError(error as ApiError);

      if (error instanceof ApiError) {
        if (error.message) {
          addNotification({ id: nanoid(8), type: 'ERROR', content: error.message });
        }
        if (error.status === 'FETCH_ERROR') {
          addNotification({ id: 'fetch_error', content: t('errors.fetch_error'), type: 'ERROR' });
          throw error;
        }
        if (error.status === 500 && error.details) {
          setApiError(error.details);
          throw error;
        }
      }

      throw error;
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [mutation, options?.resetQueryKeys]);

  const reset = useCallback(() => {
    abort();
    setData(undefined);
    setError(undefined);
  }, [abort]);

  return { data: data ?? undefined, error, isError: !!error, isSuccess: !!data && !error, isLoading, mutate: fetchData, reset, abort };
};

const useLazyQuery = <T>(mutation: (...args: any[]) => Promise<T>) => {
  const data = useMutation(mutation);
  return {
    ...data,
    load: data.mutate,
  };
};

export { useQuery, useMutation, useLazyQuery, isAbortError };
export type { QueryFn, MutationFn };
